import produce from 'immer';
import uniq from 'lodash/uniq';
import type {
	BoundActions,
	HookActionsFunction,
	HookFunction,
	OverrideContainerComponent,
} from 'react-sweet-state';
import { createActionsHook, createStore, createHook, createContainer } from 'react-sweet-state';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { isSpaceRestrictedError } from '@confluence/restrictions/entry-points/Query.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { isExpectedApolloError } from '@confluence/error-boundary/entry-points/expectedGraphQlErrorHelper.ts';
import { isTooManyRequestsError } from '@confluence/error-types/entry-points/isTooManyRequestsError.ts';
import { isSmartFolder } from '@confluence/smart-folder/entry-points/isSmartFolder.ts';
import {
	moveItemOnTree,
	addChild,
	removeChild,
} from '@confluence/tree/entry-points/treeMutations.ts';
import type {
	ItemId,
	TreeObjectItems,
	TreeObject,
} from '@confluence/tree/entry-points/tree-types.ts';

import type { ContentTreeItem } from './data-transformers.ts';
import {
	createTreeItem,
	deriveStateFromData,
	isManagedFolder,
	setChildren,
	transformUIAttributes,
} from './data-transformers.ts';
import { VIRTUAL_LOCAL_ROOT_ID, VIRTUAL_ROOT_ID } from './virtualRootIds.js';
import {
	childrenAreLatest,
	getAncestryIds,
	getIds,
	getPages,
	mergePages,
} from './data-extractors.ts';

export const initialPageTreeState: PageTreeState = {
	expandedPages: new Set(),
	loadingPages: new Set(),
	pages: {},
	space: null,
	spaceKey: null,
	rootId: null,
	isTreeTruncatedPrevious: false,
	isTreeTruncatedFollowing: false,
	cursors: {},
	initialPages: {},
	error: null,
	blogPeekRoot: undefined,
	focusedPageId: null,
	isPageTreeLoading: true,
	contentIdsWithLoadedChildren: new Set(),
};

export let persistedPageTreeStateObject: PageTreeState = initialPageTreeState;

export type PageTreeState = {
	expandedPages: Set<ItemId>; // A set of nodes that are currently expanded in the UI.
	loadingPages: Set<ItemId>;
	pages: TreeObjectItems<ContentTreeItem>;
	space: {
		id: string;
		homepage: { id: string };
		operations: Array<{ targetType: string; operation: string }> | null;
	} | null;
	spaceKey?: string | null | undefined;
	rootId: ItemId | null;
	isTreeTruncatedPrevious: boolean;
	isTreeTruncatedFollowing: boolean;
	cursors: object;
	initialPages: object;
	isPageTreeLoading: boolean;
	error?:
		| {
				error: Error;
				isExpected?: boolean;
				// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals -- loading error metadata field, not the browser `location` global
				location?: loadingErrorLocation;
		  }
		| null
		| undefined;
	blogPeekRoot?: string | undefined;
	focusedPageId: string | null;
	contentIdsWithLoadedChildren: Set<ItemId>; // A set of all nodes that have been previously expanded.
};

export type loadingErrorLocation = 'following' | 'previous' | 'children';

type QueryChild = { id: ItemId };

type QueryChildrenResult = {
	nodes: QueryChild[];
	error?: unknown;
};

export type QueryChildren = (
	pageId: ItemId | number,
	after?: string,
	allChildren?: { count?: number; nodes: QueryChild[] },
	iteration?: number,
) => Promise<QueryChildrenResult>;

type ExpandPageOptions = {
	forceRefresh?: boolean;
	/**
	 * When true, the onExpand analytics callback is not fired even if children are loaded.
	 * Used for navigation-triggered expansions (e.g. coordinator expanding the current page after
	 * a route change) to avoid double-firing the analytics event — the user-initiated click handler
	 * already fires it via the expandPage action.
	 */
	suppressAnalytics?: boolean;
};

export const shouldUsePersistence = (spaceKey: string): boolean =>
	Object.keys(persistedPageTreeStateObject.pages).length > 0 && //there are pages in the package level variable
	persistedPageTreeStateObject.spaceKey === spaceKey; //and the persisted state has the same spacekey as the props

const toError = (error: unknown): Error =>
	error instanceof Error ? error : new Error(String(error));

const isApolloErrorInterop = (error: Error): error is ApolloErrorInterop =>
	'graphQLErrors' in error || 'networkError' in error;

const isExpectedPageTreeLoadError = (error: Error): error is ApolloErrorInterop =>
	isApolloErrorInterop(error) &&
	(isSpaceRestrictedError(error) || isExpectedApolloError(error) || isTooManyRequestsError(error));

export const actions = {
	initializeState:
		() =>
		(
			{
				setState,
				dispatch,
			}: {
				setState: any;
				dispatch: any;
			},
			{
				currentPageId,
				spaceKey,
				highlightPages = [],
				initialData,
				isPeekingFromBlogs,
				syntheticItem,
				syntheticItemIsDraft,
				detached,
			}: {
				currentPageId: any;
				spaceKey: any;
				highlightPages?: never[] | undefined;
				initialData: any;
				isPeekingFromBlogs: any;
				syntheticItem: any;
				syntheticItemIsDraft: any;
				detached: any;
			},
		): void => {
			if (shouldUsePersistence(spaceKey) && !detached) {
				setState({
					...persistedPageTreeStateObject,
					pages: transformUIAttributes(
						persistedPageTreeStateObject.pages,
						currentPageId,
						persistedPageTreeStateObject.expandedPages,
						persistedPageTreeStateObject.loadingPages,
						highlightPages,
					),
				});
			} else if (initialData && Object.keys(initialData).length > 0) {
				try {
					const stateFromData = deriveStateFromData(
						initialData,
						spaceKey,
						{
							...initialPageTreeState,
							expandedPages: new Set(),
							loadingPages: new Set(),
						},
						isPeekingFromBlogs,
						syntheticItem,
						syntheticItemIsDraft,
					);
					dispatch(actions.setStateWithUIAttributes(stateFromData));
					dispatch(actions.setIsPageTreeLoading(false));
				} catch (error) {
					dispatch(actions.handleLoadingError(toError(error)));
				}
			} else {
				setState({
					...initialPageTreeState,
					expandedPages: new Set(),
					loadingPages: new Set(),
					spaceKey,
				});
			}
		},
	getState:
		() =>
		({ getState }: { getState: any }): any => {
			return getState();
		},
	//only fires when props change on the container
	derivedStateFromPropsReplacement:
		() =>
		(
			{
				dispatch,
				getState,
			}: {
				dispatch: any;
				getState: any;
			},
			{
				currentPageId,
				highlightPages = [],
				spaceKey,
			}: {
				currentPageId: any;
				highlightPages?: never[] | undefined;
				spaceKey: any;
			},
		): void => {
			//should really check if any of these props changed
			const { pages, expandedPages, loadingPages } = getState();

			dispatch(
				actions.setStateWithUIAttributes({
					pages: transformUIAttributes(
						pages,
						currentPageId,
						expandedPages,
						loadingPages,
						highlightPages,
					),
					spaceKey,
				}),
			);
		},
	updatePersistedState:
		() =>
		(
			{
				getState,
			}: {
				getState: any;
			},
			{
				detached,
			}: {
				detached: any;
			},
		): void => {
			if (!detached) {
				//don't save state if we're in the move page dialog
				const state = getState();
				persistedPageTreeStateObject = state;
			}
		},
	setStateWithUIAttributes:
		(stateObj: any) =>
		(
			{
				setState,
				getState,
				dispatch,
			}: {
				setState: any;
				getState: any;
				dispatch: any;
			},
			{
				currentPageId,
				highlightPages = [],
			}: {
				currentPageId: any;
				highlightPages?: never[] | undefined;
			},
		): void => {
			//this is a brute force access to state, it kinda doesn't follow the conventions of sweet state...buuut the alternative is a lot of very samey setters
			setState({ ...stateObj });
			const { pages, expandedPages, loadingPages, cursors } = getState();
			//this function allows us to transformUIAttributes universally on update
			//toDo: move these properties to state and simply read the sweet state via hooks lower in the tree rather than updating the pages state
			setState({
				//this is
				pages: transformUIAttributes(
					pages,
					currentPageId,
					expandedPages,
					loadingPages,
					highlightPages,
					cursors,
				),
			});
			dispatch(actions.setFocusPageId());
		},
	addLoadingPage:
		(pageId: string) =>
		({ getState, dispatch }: { getState: any; dispatch: any }): void => {
			dispatch(
				actions.setStateWithUIAttributes({
					loadingPages: getState().loadingPages.add(pageId),
				}),
			);
		},
	setIsPageTreeLoading:
		(bool: boolean) =>
		({ setState }: { setState: any }): void => {
			setState({ isPageTreeLoading: bool });
		},
	setIsTreeTruncated:
		(bool: boolean) =>
		({ setState }: { setState: any }): void => {
			setState({ isTreeTruncated: bool });
		},
	setIsTreeTruncatedPrevious:
		(bool: boolean) =>
		({ setState }: { setState: any }): void => {
			setState({ isTreeTruncatedPrevious: bool });
		},
	setIsTreeTruncatedFollowing:
		(bool: boolean) =>
		({ setState }: { setState: any }): void => {
			setState({ isTreeTruncatedFollowing: bool });
		},
	deleteLoadingPage:
		(pageId: string) =>
		({ getState, dispatch }: { getState: any; dispatch: any }): void => {
			const { loadingPages } = getState();
			loadingPages.delete(pageId);
			dispatch(actions.setStateWithUIAttributes({ loadingPages }));
		},
	expandPage:
		(page: ContentTreeItem, queryChildren?: QueryChildren, options?: ExpandPageOptions) =>
		async (
			{
				getState,
				setState,
				dispatch,
			}: {
				getState: any;
				setState: any;
				dispatch: any;
			},
			{
				onExpand,
			}: {
				onExpand: any;
			},
		): Promise<void> => {
			const { pages, expandedPages, loadingPages, space } = getState();
			const expandedPage = pages[page.id];
			const isForceRefresh = options?.forceRefresh ?? false;
			if (
				(expandedPages.has(page.id) && !isForceRefresh) ||
				(!expandedPage.hasChildren && expandedPage.data.type !== 'folder')
			) {
				return;
			}

			const mergedExpansions = [page.id, ...getAncestryIds(page.id, pages, new Set<ItemId>())];

			if (expandedPage.hasChildren && !childrenAreLatest(expandedPage)) {
				if (!queryChildren) {
					return;
				}

				dispatch(
					actions.setStateWithUIAttributes({
						loadingPages: new Set([...loadingPages, expandedPage.id]),
					}),
				);

				const children = {};

				const { nodes, error } = await queryChildren(expandedPage.id);

				if (error) {
					//toDo: handle loading children error here, then reset state so they can attempt to re-expand
					const loadingPagesAfterError = new Set(getState().loadingPages);
					loadingPagesAfterError.delete(expandedPage.id);
					dispatch(
						actions.setStateWithUIAttributes({
							loadingPages: loadingPagesAfterError,
						}),
					);
					return;
				}

				const queriedChildren = getPages({ nodes });
				queriedChildren.forEach((child) => {
					children[child.id] = createTreeItem(child);
				});

				// It's possible for the childen to change locally (via DnD) while the queryChildren request is in-flight.
				// Get the latest children now (after the request finished) to prevent a loss of state.
				const { pages: latestPages, loadingPages: latestLoadingPages } = getState();
				const latestChildren = latestPages[expandedPage.id].children;
				latestChildren.forEach((id) => {
					children[id] = latestPages[id];
				});

				const childIds = uniq([...getIds(queriedChildren), ...latestChildren]);

				let allPages = mergePages(latestPages, children);
				allPages = produce(allPages, (editedAllPages) => {
					// @ts-ignore hasAdditionalChildren not available in AK tree. Can remove when using pragmatic types.
					editedAllPages[expandedPage.id].hasAdditionalChildren = false;
				});

				const nextLoadingPages = new Set(latestLoadingPages);
				nextLoadingPages.delete(expandedPage.id);
				const finalPages = setChildren(allPages, expandedPage.id, childIds);
				dispatch(
					actions.setStateWithUIAttributes({
						//use dispatch to make sure that if special properties are changed the tree is re-jiggered
						loadingPages: nextLoadingPages,
						pages: finalPages,
					}),
				);

				if (onExpand && !isForceRefresh && !options?.suppressAnalytics) {
					onExpand(finalPages[expandedPage.id], { spaceId: space?.id });
				}
			}
			const {
				expandedPages: latestExpandedPages,
				contentIdsWithLoadedChildren: latestContentIdsWithLoadedChildren,
			} = getState();
			setState({
				contentIdsWithLoadedChildren: new Set([
					...latestContentIdsWithLoadedChildren,
					...mergedExpansions,
				]),
			});
			dispatch(
				actions.setStateWithUIAttributes({
					expandedPages: new Set([...latestExpandedPages, ...mergedExpansions]),
				}),
			);
		},
	expandSpaceHomepage:
		() =>
		({ getState, setState, dispatch }: { getState: any; setState: any; dispatch: any }): void => {
			const { pages, expandedPages, contentIdsWithLoadedChildren, space } = getState();
			const homepageId = space?.homepage?.id;
			if (!homepageId) {
				return;
			}
			// the home page might still not be loaded in some weirder cases
			const expandedPage = pages[homepageId];
			if (expandedPages.has(homepageId) || !expandedPage?.hasChildren) {
				return;
			}

			const mergedExpansions = [
				homepageId,
				...getAncestryIds(homepageId, pages, new Set<ItemId>()),
			];

			setState({
				contentIdsWithLoadedChildren: new Set([
					...contentIdsWithLoadedChildren,
					...mergedExpansions,
				]),
			});
			dispatch(
				actions.setStateWithUIAttributes({
					expandedPages: new Set([...expandedPages, ...mergedExpansions]),
				}),
			);
		},
	collapsePage:
		(page: ContentTreeItem) =>
		(
			{
				getState,
				dispatch,
			}: {
				getState: any;
				dispatch: any;
			},
			{
				onCollapse,
			}: {
				onCollapse: any;
			},
		): void => {
			const { expandedPages, space } = getState();
			expandedPages.delete(page.id);
			dispatch(actions.setStateWithUIAttributes({ expandedPages }));
			if (onCollapse) {
				onCollapse(page, { spaceId: space?.id });
			}
		},
	movePageInState:
		(
			pageId: ItemId,
			source: any,
			destination: any,
			queryChildren?: QueryChildren,
			expandAfter?: boolean,
		) =>
		({ getState, dispatch }: { getState: any; dispatch: any }): void => {
			const { rootId, pages, expandedPages } = getState();

			let newTree: TreeObject<ContentTreeItem> = {
				rootId,
				items: pages,
			};
			const destinationParent = newTree.items[destination.parentId];
			if (source.index === undefined) {
				newTree = removeChild(newTree, source.parentId, pageId);
				newTree = addChild(newTree, destination.parentId, pageId, destination.index);
			} else {
				newTree = moveItemOnTree<ContentTreeItem>(newTree, source, destination);
			}

			if (newTree.items[source.parentId].children.length === 0) {
				expandedPages.delete(source.parentId);
			}

			dispatch(
				actions.setStateWithUIAttributes({
					pages: newTree.items,
					rootId: newTree.rootId,
					expandedPages,
				}),
			);
			if (expandAfter) {
				dispatch(actions.expandPage(destinationParent, queryChildren));
			}
		},
	handleLoadingError:
		(error: Error | undefined, location?: loadingErrorLocation) =>
		(
			{
				setState,
			}: {
				setState: any;
			},
			{
				onLoadError,
			}: {
				onLoadError: any;
			},
		): void => {
			if (!error) {
				return;
			}

			const errorState: PageTreeState['error'] = {
				error,
				location,
			};

			if (isExpectedPageTreeLoadError(error)) {
				// Until we have a proper "restricted" state, we will show the
				// error state in case of restricted error, but mark the error
				// as expected so the experience will still succeed
				markErrorAsHandled(error);
				errorState.isExpected = true;
			}

			//todo: to handle loading children and previous next errors without causing the whole tree to crash we may want to wrap this in if(location)
			//however that means the error will not be reported and we need a programatic way of ding that. OR new places to render the error handler at the top / bottom/ as a child of tree elements
			setState({
				error: errorState,
			});

			if (onLoadError) {
				onLoadError(error, {
					isExpected: errorState.isExpected,
					errorLocation: errorState.location,
				});
			}
		},
	clearError:
		() =>
		({ setState }: { setState: any }): void => {
			setState({ error: null });
		},
	updateSinglePage:
		(id: string, data: object) =>
		({ getState, dispatch }: { getState: any; dispatch: any }): void => {
			const { pages } = getState();
			// Do nothing if we aren't tracking this page state in the pagetree
			if (!pages[id]) return;

			const pageToUpdate = {
				...pages[id],
				data: { ...pages[id].data, ...data },
			};
			dispatch(
				actions.setStateWithUIAttributes({
					pages: { ...pages, [id]: pageToUpdate },
				}),
			);
		},
	/**
	 * Invalidate the cached children of a folder so the next access pulls a
	 * fresh page from the network, and — if the folder is currently expanded —
	 * immediately re-runs the children query so the tree shows the new state
	 * without requiring the user to collapse + re-expand.
	 *
	 * Used after smart-folder sync/unsync so the synced children appear in the
	 * tree without a full page refresh.
	 */
	refreshChildren:
		(id: string, queryChildren: QueryChildren) =>
		({ getState, dispatch }: { getState: any; dispatch: any }): void => {
			const { pages, expandedPages, loadingPages } = getState();
			const page = pages[id];
			if (!page || page.data.type !== 'folder') {
				return;
			}

			// Mark children as stale so `childrenAreLatest` returns false and any
			// future expandPage call will refetch.
			const invalidatedPages = produce(pages, (draft) => {
				draft[id].hasChildren = true;
				draft[id].hasAdditionalChildren = true;
			});

			if (!expandedPages.has(id) || loadingPages.has(id)) {
				dispatch(
					actions.setStateWithUIAttributes({
						pages: invalidatedPages,
					}),
				);
				return;
			}

			// Folder is currently expanded — keep it open while the refresh runs to
			// avoid a collapse/re-expand flash, but bypass expandPage's usual
			// already-expanded early return so fresh children are fetched.
			dispatch(
				actions.setStateWithUIAttributes({
					pages: invalidatedPages,
				}),
			);
			dispatch(actions.expandPage(invalidatedPages[id], queryChildren, { forceRefresh: true }));
		},
	setFocusPageId:
		() =>
		(
			{
				getState,
				setState,
			}: {
				getState: any;
				setState: any;
			},
			{
				currentPageId,
				highlightPages = [],
			}: {
				currentPageId: any;
				highlightPages?: never[] | undefined;
			},
		): void => {
			const { blogPeekRoot } = getState();
			const focusedPageId = blogPeekRoot || currentPageId || highlightPages[0];

			setState({ focusedPageId });
		},
	getCurrentTreeSize:
		() =>
		({ getState }: { getState: any }): number => {
			const { pages } = getState();
			return Object.keys(pages).length;
		},
	optimisticInsertContent:
		({
			contentId,
			spaceKey,
			parentId,
			type,
			subType,
		}: {
			contentId: string;
			spaceKey: string;
			parentId: string | undefined;
			type: string;
			subType: string | undefined;
		}) =>
		({ getState, setState }: { getState: any; setState: any }): void => {
			const { pages, rootId, spaceKey: currentSpaceKey } = getState();

			const effectiveParentId = parentId || rootId;

			if (pages[contentId]) {
				return; // Page already exists
			}

			if (spaceKey !== currentSpaceKey) {
				return; // Content was created in a different space
			}

			if (!pages[effectiveParentId]?.isExpanded) {
				return; // At this time we only support optimistic insertions for expanded pages
			}

			if (type !== 'page' || subType) {
				return; // At this time we only support optimistic insertions for regular pages
			}

			const newPage = createTreeItem({
				id: contentId,
				parentId: effectiveParentId,
				type,
				title: '',
				status: 'DRAFT',
				ancestors: [],
				children: [],
				hasChildren: false,
				isSelected: true,
				createdDate: {
					value: new Date().getTime(),
				},
				isOptimistic: true,
			});

			const parentPage = {
				...pages[effectiveParentId],
				children: [...pages[effectiveParentId].children, contentId],
				hasChildren: true,
			};

			const newPages = {
				...pages,
				[effectiveParentId]: parentPage,
				[contentId]: newPage,
			};

			setState({
				pages: newPages,
			});
		},
	optimisticInsertSmartLinkEmbed:
		({
			contentId,
			parentId,
			title,
			url,
			iconUrl,
			queryChildren,
			position,
			movePage,
		}: {
			contentId: string;
			parentId: string;
			title?: string;
			url: string;
			iconUrl?: string;
			queryChildren?: QueryChildren;
			position?: {
				siblingId: string;
				placement: 'before' | 'after';
			};
			movePage?: (params: {
				pageId: string;
				siblingId: string;
				positionToSibling: 'before' | 'after';
			}) => Promise<void>;
		}) =>
		async ({
			getState,
			setState,
			dispatch,
		}: {
			getState: any;
			setState: any;
			dispatch: any;
		}): Promise<void> => {
			const { pages, rootId } = getState();

			const effectiveParentId = parentId || rootId;

			if (pages[contentId]) {
				return; // Content already exists in the tree
			}

			if (!effectiveParentId || !pages[effectiveParentId]) {
				return; // Parent doesn't exist in the tree
			}

			const { spaceKey } = getState();

			const newItem = createTreeItem({
				id: contentId,
				parentId: effectiveParentId,
				type: 'embed',
				title: title || url,
				status: 'CURRENT',
				links: {
					webui: `/spaces/${spaceKey}/embed/${contentId}`,
				},
				ancestors: [],
				children: [],
				hasChildren: false,
				isSelected: false,
				createdDate: {
					value: new Date().getTime(),
				},
				isOptimistic: true,
			});

			// Set the icon URL as the emoji field directly on the tree item data.
			// createTreeItem extracts emoji from page.metadata.properties, which we don't have
			// for optimistic inserts. For embeds, the emoji field stores the favicon URL.
			if (iconUrl) {
				newItem.data.emoji = iconUrl;
			}

			const newPages = {
				...pages,
				[effectiveParentId]: {
					...pages[effectiveParentId],
					children: [...pages[effectiveParentId].children, contentId],
					hasChildren: true,
				},
				[contentId]: newItem,
			};

			setState({
				pages: newPages,
			});

			// Expand the parent so the newly inserted item is visible
			const parentPage = newPages[effectiveParentId];
			if (!parentPage.isExpanded) {
				dispatch(actions.expandPage(parentPage, queryChildren));
			}

			// Reposition to the exact drop location if a sibling position was specified
			if (position && movePage) {
				const { siblingId, placement } = position;
				const targetOffset = placement === 'before' ? 0 : 1;

				const { pages: currentPages } = getState();
				const parentChildren = currentPages[effectiveParentId]?.children || [];
				const siblingIndex = parentChildren.indexOf(siblingId);
				if (siblingIndex >= 0) {
					dispatch(
						actions.movePageInState(
							contentId,
							{ parentId: effectiveParentId, index: parentChildren.length - 1 },
							{ parentId: effectiveParentId, index: siblingIndex + targetOffset },
							undefined,
						),
					);
				}
				await movePage({
					pageId: contentId,
					siblingId,
					positionToSibling: placement,
				});
			}
		},
	optimisticDeleteContent:
		(contentId: any, deleteCallback: any) =>
		({ getState, setState }: { getState: any; setState: any }): void => {
			const { pages, rootId } = getState();
			const targetPage = pages?.[contentId];
			const parentId = targetPage?.parentId || rootId;
			if (!targetPage || !parentId || !pages[parentId]?.children) {
				if (deleteCallback) {
					deleteCallback();
				}
				return; // Page doesn't exist, parent doesn't exist, or parent has no children
			}

			const parentPage = {
				...pages[parentId],
				children: pages[parentId].children.filter((childId) => childId !== contentId),
				hasChildren: pages[parentId].children.length > 1,
			};

			//copy pages minus the targetPage
			const { [contentId]: _deletedKey, ...otherPages } = pages;
			const newPages = {
				...otherPages,
				[parentId]: parentPage,
			};

			setState({ pages: newPages });
			if (deleteCallback) {
				deleteCallback();
			}
		},
};

const Store = createStore({
	// @ts-ignore
	initialState: {
		...initialPageTreeState,
		expandedPages: new Set(),
		loadingPages: new Set(),
	},
	actions,
	name: 'pageTreeState',
});

//use as a component & pass in these props
// currentPageId,
// highlightPages,
// spaceKey,
// syntheticItem,
// syntheticItemIsDraft,
// onExpand,
// onCollapse
// onLoadError,
// isPeekingFromBlogs,
// initialData
export const PageTreeStateContainer: OverrideContainerComponent<{
	currentPageId: any;
	spaceKey: any;
	highlightPages?: never[] | undefined;
	initialData: any;
	isPeekingFromBlogs: any;
	syntheticItem: any;
	syntheticItemIsDraft: any;
	detached: any;
}> = createContainer(Store, {
	onInit: actions.initializeState,
	// @ts-ignore
	onUpdate: actions.derivedStateFromPropsReplacement,
});

//state hook
export const usePageTreeState: any = createHook(Store);
export const usePageTreeActions: HookActionsFunction<
	BoundActions<
		{
			expandedPages: Set<unknown>;
			loadingPages: Set<unknown>;
			pages: TreeObjectItems<ContentTreeItem>;
			space: {
				id: string;
				homepage: { id: string };
				operations: Array<{ targetType: string; operation: string }> | null;
			} | null;
			spaceKey?: string | null | undefined;
			rootId: ItemId | null;
			isTreeTruncatedPrevious: boolean;
			isTreeTruncatedFollowing: boolean;
			cursors: object;
			initialPages: object;
			isPageTreeLoading: boolean;
			error?:
				| {
						error: Error;
						isExpected?: boolean;
						// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals -- loading error metadata field, not the browser `location` global
						location?: loadingErrorLocation;
				  }
				| null
				| undefined;
			blogPeekRoot?: string | undefined;
			focusedPageId: string | null;
			contentIdsWithLoadedChildren: Set<ItemId>; // A set of all nodes that have been previously expanded.
		},
		{
			initializeState: () => (
				{
					setState,
					dispatch,
				}: {
					setState: any;
					dispatch: any;
				},
				{
					currentPageId,
					spaceKey,
					highlightPages,
					initialData,
					isPeekingFromBlogs,
					syntheticItem,
					syntheticItemIsDraft,
					detached,
				}: {
					currentPageId: any;
					spaceKey: any;
					highlightPages?: never[] | undefined;
					initialData: any;
					isPeekingFromBlogs: any;
					syntheticItem: any;
					syntheticItemIsDraft: any;
					detached: any;
				},
			) => void;
			getState: () => ({ getState }: { getState: any }) => any;
			//only fires when props change on the container
			derivedStateFromPropsReplacement: () => (
				{
					dispatch,
					getState,
				}: {
					dispatch: any;
					getState: any;
				},
				{
					currentPageId,
					highlightPages,
					spaceKey,
				}: {
					currentPageId: any;
					highlightPages?: never[] | undefined;
					spaceKey: any;
				},
			) => void;
			updatePersistedState: () => (
				{
					getState,
				}: {
					getState: any;
				},
				{
					detached,
				}: {
					detached: any;
				},
			) => void;
			setStateWithUIAttributes: (stateObj: any) => (
				{
					setState,
					getState,
					dispatch,
				}: {
					setState: any;
					getState: any;
					dispatch: any;
				},
				{
					currentPageId,
					highlightPages,
				}: {
					currentPageId: any;
					highlightPages?: never[] | undefined;
				},
			) => void;
			addLoadingPage: (
				pageId: string,
			) => ({ getState, dispatch }: { getState: any; dispatch: any }) => void;
			setIsPageTreeLoading: (bool: boolean) => ({ setState }: { setState: any }) => void;
			setIsTreeTruncated: (bool: boolean) => ({ setState }: { setState: any }) => void;
			setIsTreeTruncatedPrevious: (bool: boolean) => ({ setState }: { setState: any }) => void;
			setIsTreeTruncatedFollowing: (bool: boolean) => ({ setState }: { setState: any }) => void;
			deleteLoadingPage: (
				pageId: string,
			) => ({ getState, dispatch }: { getState: any; dispatch: any }) => void;
			expandPage: (
				page: ContentTreeItem,
				queryChildren?: QueryChildren,
				options?: ExpandPageOptions,
			) => (
				{
					getState,
					setState,
					dispatch,
				}: {
					getState: any;
					setState: any;
					dispatch: any;
				},
				{
					onExpand,
				}: {
					onExpand: any;
				},
			) => Promise<void>;
			expandSpaceHomepage: () => ({
				getState,
				setState,
				dispatch,
			}: {
				getState: any;
				setState: any;
				dispatch: any;
			}) => void;
			collapsePage: (page: ContentTreeItem) => (
				{
					getState,
					dispatch,
				}: {
					getState: any;
					dispatch: any;
				},
				{
					onCollapse,
				}: {
					onCollapse: any;
				},
			) => void;
			movePageInState: (
				pageId: ItemId,
				source: any,
				destination: any,
				queryChildren?: QueryChildren,
				expandAfter?: boolean,
			) => ({ getState, dispatch }: { getState: any; dispatch: any }) => void;
			handleLoadingError: (
				error: Error | undefined,
				location?: loadingErrorLocation,
			) => (
				{
					setState,
				}: {
					setState: any;
				},
				{
					onLoadError,
				}: {
					onLoadError: any;
				},
			) => void;
			clearError: () => ({ setState }: { setState: any }) => void;
			updateSinglePage: (
				id: string,
				data: object,
			) => ({ getState, dispatch }: { getState: any; dispatch: any }) => void;
			/**
			 * Invalidate the cached children of a folder so the next access pulls a
			 * fresh page from the network, and — if the folder is currently expanded —
			 * immediately re-runs the children query so the tree shows the new state
			 * without requiring the user to collapse + re-expand.
			 *
			 * Used after smart-folder sync/unsync so the synced children appear in the
			 * tree without a full page refresh.
			 */
			refreshChildren: (
				id: string,
				queryChildren: QueryChildren,
			) => ({ getState, dispatch }: { getState: any; dispatch: any }) => void;
			setFocusPageId: () => (
				{
					getState,
					setState,
				}: {
					getState: any;
					setState: any;
				},
				{
					currentPageId,
					highlightPages,
				}: {
					currentPageId: any;
					highlightPages?: never[] | undefined;
				},
			) => void;
			getCurrentTreeSize: () => ({ getState }: { getState: any }) => number;
			optimisticInsertContent: ({
				contentId,
				spaceKey,
				parentId,
				type,
				subType,
			}: {
				contentId: string;
				spaceKey: string;
				parentId: string | undefined;
				type: string;
				subType: string | undefined;
			}) => ({ getState, setState }: { getState: any; setState: any }) => void;
			optimisticInsertSmartLinkEmbed: ({
				contentId,
				parentId,
				title,
				url,
				iconUrl,
				queryChildren,
				position,
				movePage,
			}: {
				contentId: string;
				parentId: string;
				title?: string;
				url: string;
				iconUrl?: string;
				queryChildren?: QueryChildren;
				position?: {
					siblingId: string;
					placement: 'before' | 'after';
				};
				movePage?: (params: {
					pageId: string;
					siblingId: string;
					positionToSibling: 'before' | 'after';
				}) => Promise<void>;
			}) => ({
				getState,
				setState,
				dispatch,
			}: {
				getState: any;
				setState: any;
				dispatch: any;
			}) => Promise<void>;
			optimisticDeleteContent: (
				contentId: any,
				deleteCallback: any,
			) => ({ getState, setState }: { getState: any; setState: any }) => void;
		}
	>
> = createActionsHook(Store);

export const useContentIdsWithLoadedChildren: HookFunction<
	Set<string>,
	BoundActions<
		{
			expandedPages: Set<unknown>;
			loadingPages: Set<unknown>;
			pages: TreeObjectItems<ContentTreeItem>;
			space: {
				id: string;
				homepage: { id: string };
				operations: Array<{ targetType: string; operation: string }> | null;
			} | null;
			spaceKey?: string | null | undefined;
			rootId: ItemId | null;
			isTreeTruncatedPrevious: boolean;
			isTreeTruncatedFollowing: boolean;
			cursors: object;
			initialPages: object;
			isPageTreeLoading: boolean;
			error?:
				| {
						error: Error;
						isExpected?: boolean;
						// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals -- loading error metadata field, not the browser `location` global
						location?: loadingErrorLocation;
				  }
				| null
				| undefined;
			blogPeekRoot?: string | undefined;
			focusedPageId: string | null;
			contentIdsWithLoadedChildren: Set<ItemId>; // A set of all nodes that have been previously expanded.
		},
		{
			initializeState: () => (
				{
					setState,
					dispatch,
				}: {
					setState: any;
					dispatch: any;
				},
				{
					currentPageId,
					spaceKey,
					highlightPages,
					initialData,
					isPeekingFromBlogs,
					syntheticItem,
					syntheticItemIsDraft,
					detached,
				}: {
					currentPageId: any;
					spaceKey: any;
					highlightPages?: never[] | undefined;
					initialData: any;
					isPeekingFromBlogs: any;
					syntheticItem: any;
					syntheticItemIsDraft: any;
					detached: any;
				},
			) => void;
			getState: () => ({ getState }: { getState: any }) => any;
			//only fires when props change on the container
			derivedStateFromPropsReplacement: () => (
				{
					dispatch,
					getState,
				}: {
					dispatch: any;
					getState: any;
				},
				{
					currentPageId,
					highlightPages,
					spaceKey,
				}: {
					currentPageId: any;
					highlightPages?: never[] | undefined;
					spaceKey: any;
				},
			) => void;
			updatePersistedState: () => (
				{
					getState,
				}: {
					getState: any;
				},
				{
					detached,
				}: {
					detached: any;
				},
			) => void;
			setStateWithUIAttributes: (stateObj: any) => (
				{
					setState,
					getState,
					dispatch,
				}: {
					setState: any;
					getState: any;
					dispatch: any;
				},
				{
					currentPageId,
					highlightPages,
				}: {
					currentPageId: any;
					highlightPages?: never[] | undefined;
				},
			) => void;
			addLoadingPage: (
				pageId: string,
			) => ({ getState, dispatch }: { getState: any; dispatch: any }) => void;
			setIsPageTreeLoading: (bool: boolean) => ({ setState }: { setState: any }) => void;
			setIsTreeTruncated: (bool: boolean) => ({ setState }: { setState: any }) => void;
			setIsTreeTruncatedPrevious: (bool: boolean) => ({ setState }: { setState: any }) => void;
			setIsTreeTruncatedFollowing: (bool: boolean) => ({ setState }: { setState: any }) => void;
			deleteLoadingPage: (
				pageId: string,
			) => ({ getState, dispatch }: { getState: any; dispatch: any }) => void;
			expandPage: (
				page: ContentTreeItem,
				queryChildren?: QueryChildren,
				options?: ExpandPageOptions,
			) => (
				{
					getState,
					setState,
					dispatch,
				}: {
					getState: any;
					setState: any;
					dispatch: any;
				},
				{
					onExpand,
				}: {
					onExpand: any;
				},
			) => Promise<void>;
			expandSpaceHomepage: () => ({
				getState,
				setState,
				dispatch,
			}: {
				getState: any;
				setState: any;
				dispatch: any;
			}) => void;
			collapsePage: (page: ContentTreeItem) => (
				{
					getState,
					dispatch,
				}: {
					getState: any;
					dispatch: any;
				},
				{
					onCollapse,
				}: {
					onCollapse: any;
				},
			) => void;
			movePageInState: (
				pageId: ItemId,
				source: any,
				destination: any,
				queryChildren?: QueryChildren,
				expandAfter?: boolean,
			) => ({ getState, dispatch }: { getState: any; dispatch: any }) => void;
			handleLoadingError: (
				error: Error | undefined,
				location?: loadingErrorLocation,
			) => (
				{
					setState,
				}: {
					setState: any;
				},
				{
					onLoadError,
				}: {
					onLoadError: any;
				},
			) => void;
			clearError: () => ({ setState }: { setState: any }) => void;
			updateSinglePage: (
				id: string,
				data: object,
			) => ({ getState, dispatch }: { getState: any; dispatch: any }) => void;
			/**
			 * Invalidate the cached children of a folder so the next access pulls a
			 * fresh page from the network, and — if the folder is currently expanded —
			 * immediately re-runs the children query so the tree shows the new state
			 * without requiring the user to collapse + re-expand.
			 *
			 * Used after smart-folder sync/unsync so the synced children appear in the
			 * tree without a full page refresh.
			 */
			refreshChildren: (
				id: string,
				queryChildren: QueryChildren,
			) => ({ getState, dispatch }: { getState: any; dispatch: any }) => void;
			setFocusPageId: () => (
				{
					getState,
					setState,
				}: {
					getState: any;
					setState: any;
				},
				{
					currentPageId,
					highlightPages,
				}: {
					currentPageId: any;
					highlightPages?: never[] | undefined;
				},
			) => void;
			getCurrentTreeSize: () => ({ getState }: { getState: any }) => number;
			optimisticInsertContent: ({
				contentId,
				spaceKey,
				parentId,
				type,
				subType,
			}: {
				contentId: string;
				spaceKey: string;
				parentId: string | undefined;
				type: string;
				subType: string | undefined;
			}) => ({ getState, setState }: { getState: any; setState: any }) => void;
			optimisticInsertSmartLinkEmbed: ({
				contentId,
				parentId,
				title,
				url,
				iconUrl,
				queryChildren,
				position,
				movePage,
			}: {
				contentId: string;
				parentId: string;
				title?: string;
				url: string;
				iconUrl?: string;
				queryChildren?: QueryChildren;
				position?: {
					siblingId: string;
					placement: 'before' | 'after';
				};
				movePage?: (params: {
					pageId: string;
					siblingId: string;
					positionToSibling: 'before' | 'after';
				}) => Promise<void>;
			}) => ({
				getState,
				setState,
				dispatch,
			}: {
				getState: any;
				setState: any;
				dispatch: any;
			}) => Promise<void>;
			optimisticDeleteContent: (
				contentId: any,
				deleteCallback: any,
			) => ({ getState, setState }: { getState: any; setState: any }) => void;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.contentIdsWithLoadedChildren,
});

// True when the given page's immediate parent is a managed (smart) folder.
export const useHasManagedParent: any = createHook(Store, {
	selector: (state, itemId: string) => {
		const parentId = state.pages[itemId]?.parentId;
		if (!parentId) {
			return false;
		}
		return isSmartFolder(state.pages[parentId]?.data ?? {});
	},
});

export type MoveActionsCapability = {
	canMoveUp: boolean;
	canMoveDown: boolean;
	canMoveOut: boolean;
	canMoveInto: boolean;
	canMoveToTop: boolean;
	canMoveToBottom: boolean;
};

export const findParentId = (
	pages: TreeObjectItems<ContentTreeItem>,
	itemId: string,
): string | undefined => {
	const declaredParentId = pages[itemId]?.parentId;
	if (declaredParentId && pages[declaredParentId]?.children?.includes(itemId)) {
		return declaredParentId;
	}
	return Object.keys(pages).find((id) => pages[id]?.children?.includes(itemId));
};

const NO_MOVE_ACTIONS: MoveActionsCapability = {
	canMoveUp: false,
	canMoveDown: false,
	canMoveOut: false,
	canMoveInto: false,
	canMoveToTop: false,
	canMoveToBottom: false,
};

export const computeMoveActionsCapability = (
	pages: TreeObjectItems<ContentTreeItem>,
	itemId: string,
): MoveActionsCapability => {
	const item = pages[itemId];
	const parentId = item ? findParentId(pages, itemId) : undefined;
	const parent = parentId ? pages[parentId] : undefined;
	const siblings = parent?.children ?? [];
	const index = siblings.indexOf(itemId);

	if (!item || !parentId || !parent || index === -1) {
		return NO_MOVE_ACTIONS;
	}

	if (isManagedFolder(parent.data)) {
		return NO_MOVE_ACTIONS;
	}

	const grandparentId = findParentId(pages, parentId);
	const canMoveOut =
		!!grandparentId &&
		grandparentId !== VIRTUAL_ROOT_ID &&
		grandparentId !== VIRTUAL_LOCAL_ROOT_ID &&
		!!pages[grandparentId];

	let canMoveInto = false;
	if (index > 0) {
		const previousSibling = pages[siblings[index - 1]];
		canMoveInto =
			!!previousSibling &&
			!isManagedFolder(previousSibling.data) &&
			previousSibling.data.status === 'CURRENT';
	}

	return {
		canMoveUp: index > 0,
		canMoveDown: index < siblings.length - 1,
		canMoveOut,
		canMoveInto,
		canMoveToTop: index > 0,
		canMoveToBottom: index < siblings.length - 1,
	};
};

export const useMoveActionsCapability: HookFunction<
	MoveActionsCapability,
	BoundActions<
		{
			expandedPages: Set<unknown>;
			loadingPages: Set<unknown>;
			pages: TreeObjectItems<ContentTreeItem>;
			space: {
				id: string;
				homepage: { id: string };
				operations: Array<{ targetType: string; operation: string }> | null;
			} | null;
			spaceKey?: string | null | undefined;
			rootId: ItemId | null;
			isTreeTruncatedPrevious: boolean;
			isTreeTruncatedFollowing: boolean;
			cursors: object;
			initialPages: object;
			isPageTreeLoading: boolean;
			error?:
				| {
						error: Error;
						isExpected?: boolean;
						// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals -- loading error metadata field, not the browser `location` global
						location?: loadingErrorLocation;
				  }
				| null
				| undefined;
			blogPeekRoot?: string | undefined;
			focusedPageId: string | null;
			contentIdsWithLoadedChildren: Set<ItemId>; // A set of all nodes that have been previously expanded.
		},
		{
			initializeState: () => (
				{
					setState,
					dispatch,
				}: {
					setState: any;
					dispatch: any;
				},
				{
					currentPageId,
					spaceKey,
					highlightPages,
					initialData,
					isPeekingFromBlogs,
					syntheticItem,
					syntheticItemIsDraft,
					detached,
				}: {
					currentPageId: any;
					spaceKey: any;
					highlightPages?: never[] | undefined;
					initialData: any;
					isPeekingFromBlogs: any;
					syntheticItem: any;
					syntheticItemIsDraft: any;
					detached: any;
				},
			) => void;
			getState: () => ({ getState }: { getState: any }) => any;
			//only fires when props change on the container
			derivedStateFromPropsReplacement: () => (
				{
					dispatch,
					getState,
				}: {
					dispatch: any;
					getState: any;
				},
				{
					currentPageId,
					highlightPages,
					spaceKey,
				}: {
					currentPageId: any;
					highlightPages?: never[] | undefined;
					spaceKey: any;
				},
			) => void;
			updatePersistedState: () => (
				{
					getState,
				}: {
					getState: any;
				},
				{
					detached,
				}: {
					detached: any;
				},
			) => void;
			setStateWithUIAttributes: (stateObj: any) => (
				{
					setState,
					getState,
					dispatch,
				}: {
					setState: any;
					getState: any;
					dispatch: any;
				},
				{
					currentPageId,
					highlightPages,
				}: {
					currentPageId: any;
					highlightPages?: never[] | undefined;
				},
			) => void;
			addLoadingPage: (
				pageId: string,
			) => ({ getState, dispatch }: { getState: any; dispatch: any }) => void;
			setIsPageTreeLoading: (bool: boolean) => ({ setState }: { setState: any }) => void;
			setIsTreeTruncated: (bool: boolean) => ({ setState }: { setState: any }) => void;
			setIsTreeTruncatedPrevious: (bool: boolean) => ({ setState }: { setState: any }) => void;
			setIsTreeTruncatedFollowing: (bool: boolean) => ({ setState }: { setState: any }) => void;
			deleteLoadingPage: (
				pageId: string,
			) => ({ getState, dispatch }: { getState: any; dispatch: any }) => void;
			expandPage: (
				page: ContentTreeItem,
				queryChildren?: QueryChildren,
				options?: ExpandPageOptions,
			) => (
				{
					getState,
					setState,
					dispatch,
				}: {
					getState: any;
					setState: any;
					dispatch: any;
				},
				{
					onExpand,
				}: {
					onExpand: any;
				},
			) => Promise<void>;
			expandSpaceHomepage: () => ({
				getState,
				setState,
				dispatch,
			}: {
				getState: any;
				setState: any;
				dispatch: any;
			}) => void;
			collapsePage: (page: ContentTreeItem) => (
				{
					getState,
					dispatch,
				}: {
					getState: any;
					dispatch: any;
				},
				{
					onCollapse,
				}: {
					onCollapse: any;
				},
			) => void;
			movePageInState: (
				pageId: ItemId,
				source: any,
				destination: any,
				queryChildren?: QueryChildren,
				expandAfter?: boolean,
			) => ({ getState, dispatch }: { getState: any; dispatch: any }) => void;
			handleLoadingError: (
				error: Error | undefined,
				location?: loadingErrorLocation,
			) => (
				{
					setState,
				}: {
					setState: any;
				},
				{
					onLoadError,
				}: {
					onLoadError: any;
				},
			) => void;
			clearError: () => ({ setState }: { setState: any }) => void;
			updateSinglePage: (
				id: string,
				data: object,
			) => ({ getState, dispatch }: { getState: any; dispatch: any }) => void;
			/**
			 * Invalidate the cached children of a folder so the next access pulls a
			 * fresh page from the network, and — if the folder is currently expanded —
			 * immediately re-runs the children query so the tree shows the new state
			 * without requiring the user to collapse + re-expand.
			 *
			 * Used after smart-folder sync/unsync so the synced children appear in the
			 * tree without a full page refresh.
			 */
			refreshChildren: (
				id: string,
				queryChildren: QueryChildren,
			) => ({ getState, dispatch }: { getState: any; dispatch: any }) => void;
			setFocusPageId: () => (
				{
					getState,
					setState,
				}: {
					getState: any;
					setState: any;
				},
				{
					currentPageId,
					highlightPages,
				}: {
					currentPageId: any;
					highlightPages?: never[] | undefined;
				},
			) => void;
			getCurrentTreeSize: () => ({ getState }: { getState: any }) => number;
			optimisticInsertContent: ({
				contentId,
				spaceKey,
				parentId,
				type,
				subType,
			}: {
				contentId: string;
				spaceKey: string;
				parentId: string | undefined;
				type: string;
				subType: string | undefined;
			}) => ({ getState, setState }: { getState: any; setState: any }) => void;
			optimisticInsertSmartLinkEmbed: ({
				contentId,
				parentId,
				title,
				url,
				iconUrl,
				queryChildren,
				position,
				movePage,
			}: {
				contentId: string;
				parentId: string;
				title?: string;
				url: string;
				iconUrl?: string;
				queryChildren?: QueryChildren;
				position?: {
					siblingId: string;
					placement: 'before' | 'after';
				};
				movePage?: (params: {
					pageId: string;
					siblingId: string;
					positionToSibling: 'before' | 'after';
				}) => Promise<void>;
			}) => ({
				getState,
				setState,
				dispatch,
			}: {
				getState: any;
				setState: any;
				dispatch: any;
			}) => Promise<void>;
			optimisticDeleteContent: (
				contentId: any,
				deleteCallback: any,
			) => ({ getState, setState }: { getState: any; setState: any }) => void;
		}
	>,
	string
> = createHook(Store, {
	selector: (state, itemId: string): MoveActionsCapability =>
		computeMoveActionsCapability(state.pages, itemId),
});
